import React, { useEffect, useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getReportClassList } from "hooks/useFilterPersistence/useFilterPersistence";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faStar,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { colors, constants } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { saveFilters, resolveClassId } from "utils/persistFilters";
import { SelectDropdown, ClearAllButton } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { TagLevelSmall } from "components/shared/TagLevelSmall/TagLevelSmall";
import { FeedbackCTA } from "components/shared/FeedbackCTA/FeedbackCTA";
import { BadgeXSmall } from "components/shared/Badges/BadgeXSmall";
import { RubricLink } from "components/pages/Educator/StudentReports/RubricLink";
import { pageTitleInset } from "components/pageTitleInset";

const DATE_RANGE_OPTIONS = [
  { value: "last_4_weeks", label: "Last 4 weeks" },
  { value: "last_8_weeks", label: "Last 8 weeks" },
  { value: "this_school_year", label: "This school year" },
  { value: "all_time", label: "All time" },
];

const BUCKETS = [
  { level: 1, tier: "Free Agents", name: "Needs Intervention" },
  { level: 2, tier: "Practice Squad", name: "Needs Guidance" },
  { level: 3, tier: "Big Leaguers", name: "Effective Collaborator" },
  { level: 4, tier: "Dream Teamers", name: "Exceeding Expectations" },
];

const LARGE_TAG_COLORS = {
  1: { bg: colors.pink50, border: colors.pink300, text: colors.pink800 },
  2: { bg: colors.amber50, border: colors.amber700, text: colors.amber800 },
  3: { bg: colors.green50, border: colors.green300, text: colors.green700 },
  4: { bg: colors.blue50, border: colors.blue300, text: colors.blue800 },
};

const getLevelIcon = (level) => {
  if (level === 1) return faExclamationTriangle;
  if (level === 4) return faStar;
  return undefined;
};

const formatWeekLabel = (isoMonday) => {
  const d = new Date(isoMonday + "T00:00:00");
  const month = d.toLocaleDateString("en-US", { month: "short" });
  return `Week of ${month} ${d.getDate()}`;
};

const addDaysIso = (isoDate, days) => {
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const exportCsv = (data) => {
  if (!data) return;
  const { weeks, students, classroom_name } = data;
  const headers = ["Student", ...weeks.map(formatWeekLabel)];
  const rows = students.map((student) => {
    const cells = weeks.map((weekKey) => {
      const cell = student.weeks[weekKey];
      return cell ? `Level ${cell.level}` : "N/A";
    });
    return [student.student_name, ...cells];
  });
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell}"`).join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `collaboration_${classroom_name || "classroom"}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const CollaborationTrend = ({ educatorId, sharedClassId, onClassChange, embedded = false }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassIdInternal] = useState(null);

  const setSelectedClassId = (id) => {
    setSelectedClassIdInternal(id);
    if (onClassChange) onClassChange(id);
  };

  const [dateRange, setDateRange] = useState("last_4_weeks");
  const queryClient = useQueryClient();

  // Cached per (class, range): a revisited combination paints instantly while
  // revalidating in the background; the spinner only covers an uncached one
  // (OKO2-3684).
  const {
    data: heatmapData = null,
    isInitialLoading: loading,
  } = useQuery({
    queryKey: ["collaboration-heatmap", selectedClassId, dateRange, educatorId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange) params.append("date_range", dateRange);
      if (educatorId) params.append("educator_id", educatorId);
      const qs = params.toString();
      return request(
        `class-report/${selectedClassId}/collaboration-heatmap${qs ? `?${qs}` : ""}`,
        { method: "GET" }
      );
    },
    enabled: Boolean(selectedClassId),
    staleTime: 0,
  });

  const [sortBy, setSortBy] = useState("student_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    if (selectedClassId && filtersReady) {
      saveFilters("collaboration_trend", { selectedClassId });
    }
  }, [selectedClassId, filtersReady]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const classList = await getReportClassList(queryClient, educatorId);
        setClasses(classList);
        if (classList.length > 0) {
          if (sharedClassId && classList.some((c) => c.id === sharedClassId)) {
            setSelectedClassId(sharedClassId);
          } else {
            const { classId } = await resolveClassId("collaboration_trend", classList);
            setSelectedClassId(classId);
          }
        }
      } catch (err) {
        console.error("Failed to fetch classes:", err);
      } finally {
        setFiltersReady(true);
      }
    };
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educatorId, queryClient]);

  const sortedStudents = useMemo(() => {
    if (!heatmapData?.students) return [];
    if (!sortBy) return heatmapData.students;
    return [...heatmapData.students].sort((a, b) => {
      let valA, valB;
      if (sortBy === "student_name") {
        valA = a.student_name.toLowerCase();
        valB = b.student_name.toLowerCase();
      } else {
        valA = a.weeks[sortBy]?.level ?? -1;
        valB = b.weeks[sortBy]?.level ?? -1;
      }
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [heatmapData, sortBy, sortOrder]);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnKey);
      setSortOrder("asc");
    }
  };

  const handleCellClick = ({ studentId, weekKey }) => {
    const params = new URLSearchParams();
    if (selectedClassId) params.set("classroom_id", selectedClassId);
    if (studentId) params.set("student_id", studentId);
    params.set("start_date", weekKey);
    params.set("end_date", addDaysIso(weekKey, 7));
    // Session Reports opens By Lesson by default; this link is about sessions.
    params.set("tab", "groups");
    window.open(`${constants.SESSION_HISTORY_PATH}?${params.toString()}`, "_blank");
  };

  const selectedClassName =
    classDisplayName(classes.find((c) => c.id === selectedClassId)) || "";

  const weeks = heatmapData?.weeks || [];
  const hasData = !loading && heatmapData && weeks.length > 0;
  const hasStudents = hasData && heatmapData.students.length > 0;

  return (
    <>
    <Container $embedded={embedded}>
      {!embedded && (
        <Header>
          <HeaderTitle>Collaboration Trend</HeaderTitle>
        </Header>
      )}

      <FilterSection>
        <FilterCard>
          <FilterDropdowns>
            {!embedded && (
              <SelectDropdown
                label="Class"
                value={selectedClassId}
                displayText={selectedClassName || "Select class"}
                options={classes.map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
                groupBySchoolYear
                onChange={(val) => setSelectedClassId(val)}
              />
            )}

            <SelectDropdown
              label="Date Range"
              value={dateRange}
              displayText={
                DATE_RANGE_OPTIONS.find((o) => o.value === dateRange)?.label || "Select range"
              }
              options={DATE_RANGE_OPTIONS}
              onChange={(val) => setDateRange(val)}
              clearable
              clearValue="all_time"
            />
            <ClearAllButton
              onClick={() => {
                setDateRange("all_time");
              }}
            >
              Clear All
            </ClearAllButton>
          </FilterDropdowns>

          <ExportButton onClick={() => exportCsv(heatmapData)} disabled={!hasData}>
            Export .csv
          </ExportButton>
        </FilterCard>
      </FilterSection>

      <Body>
        <DescriptionSection>
          <DescriptionText>
            <strong>Building Better Collaboration:</strong> At the end of each
            question, OKO uses the{" "}
            <RubricLink
              href="https://docs.google.com/document/d/1eR3DQs4NWtr1I4wJ6UET3BdMIKTwYiaAzXnsE6Mqf2Y/edit?tab=t.0#heading=h.sa0q885n409p"
              variant="inline"
            >
              Collaboration Rubric
            </RubricLink>{" "}
            to give the group their Teamwork Score and immediate, practical
            feedback on their math talk. Your Session Report goes further,
            showing a Collaboration score, feedback, and recommended next steps
            for the group and each student.
          </DescriptionText>
        </DescriptionSection>

        <SummaryCards>
          {BUCKETS.map(({ level, tier, name }) => {
            const tagColor = LARGE_TAG_COLORS[level];
            return (
              <SummaryCard key={level}>
                <BadgeXSmall level={level} />
                <SummaryCardFooter>
                  <LargeLevelTag $bg={tagColor.bg} $border={tagColor.border} $text={tagColor.text}>
                    Level {level}: {tier}
                  </LargeLevelTag>
                  <LevelName>{name}</LevelName>
                </SummaryCardFooter>
              </SummaryCard>
            );
          })}
        </SummaryCards>

        {loading && (
          <TableContainer>
            <Table>
              <thead>
                <TableHeaderRow>
                  <SkeletonHeaderCell style={{ width: 159 }}>
                    <SkeletonBar $w="60px" />
                  </SkeletonHeaderCell>
                  {[1, 2, 3, 4].map((i) => (
                    <SkeletonHeaderCell key={i}>
                      <SkeletonBar $w="90px" />
                    </SkeletonHeaderCell>
                  ))}
                </TableHeaderRow>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <TableBodyRow key={row}>
                    <SkeletonBodyCell style={{ width: 159 }}>
                      <SkeletonBar $w="80px" />
                    </SkeletonBodyCell>
                    {[1, 2, 3, 4].map((col) => (
                      <SkeletonBodyCell key={col}>
                        <SkeletonBar $w="70px" />
                      </SkeletonBodyCell>
                    ))}
                  </TableBodyRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {hasData && (
          <TableContainer>
            <Table>
              <thead>
                <TableHeaderRow>
                  <StudentHeaderCell onClick={() => handleSort("student_name")}>
                    <span>Student</span>
                    <SortIcon column="student_name" sortBy={sortBy} sortOrder={sortOrder} />
                  </StudentHeaderCell>
                  {weeks.map((weekKey) => (
                    <WeekHeaderCell key={weekKey}>
                      <HeaderCellContent>
                        <span>{formatWeekLabel(weekKey)}</span>
                        <SortButton
                          onClick={() => handleSort(weekKey)}
                          aria-label={`Sort by ${formatWeekLabel(weekKey)}`}
                        >
                          <SortIcon column={weekKey} sortBy={sortBy} sortOrder={sortOrder} />
                        </SortButton>
                      </HeaderCellContent>
                    </WeekHeaderCell>
                  ))}
                </TableHeaderRow>
              </thead>
              <tbody>
                {hasStudents ? (
                  sortedStudents.map((student) => (
                    <TableBodyRow key={student.student_id}>
                      <StudentBodyCell>{student.student_name}</StudentBodyCell>
                      {weeks.map((weekKey) => {
                        const cell = student.weeks[weekKey];
                        if (!cell) {
                          return (
                            <LevelCell key={weekKey}>
                              <NAText>N/A</NAText>
                            </LevelCell>
                          );
                        }
                        return (
                          <LevelCell
                            key={weekKey}
                            role="button"
                            tabIndex={0}
                            aria-label={`${student.student_name}, ${weekKey}: Level ${cell.level}. View session report`}
                            onClick={() => handleCellClick({
                              studentId: student.student_id,
                              weekKey,
                            })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleCellClick({ studentId: student.student_id, weekKey });
                              }
                            }}
                            $clickable
                          >
                            <TagLevelSmall level={cell.level} icon={getLevelIcon(cell.level)} />
                          </LevelCell>
                        );
                      })}
                    </TableBodyRow>
                  ))
                ) : (
                  <TableBodyRow>
                    <EmptyRowCell colSpan={weeks.length + 1}>
                      No collaboration data available for the selected filters.
                    </EmptyRowCell>
                  </TableBodyRow>
                )}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {!loading && heatmapData && weeks.length === 0 && (
          <EmptyState>No collaboration data available for the selected filters.</EmptyState>
        )}

        <FeedbackCTA reportLocation="collaboration_trend" />
      </Body>
    </Container>
    </>
  );
};

const SortIcon = ({ column, sortBy, sortOrder }) => {
  if (sortBy === column) {
    return (
      <FontAwesomeIcon
        icon={sortOrder === "asc" ? faSortUp : faSortDown}
        style={{ opacity: 1 }}
      />
    );
  }
  return <FontAwesomeIcon icon={faSort} style={{ opacity: 0.5 }} />;
};

const Container = styled.div`
  background-color: ${colors.milkGlass200};
  min-height: ${(p) => (p.$embedded ? "0" : "100vh")};
`;

const Header = styled.div`
  background-color: ${colors.nightlyWoods500};
  padding: 24px 32px 24px ${pageTitleInset};
  height: 92px;
  display: flex;
  align-items: center;
`;

const HeaderTitle = styled.h1`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 44px;
  color: ${colors.milkGlass500};
  margin: 0;
`;

const FilterSection = styled.div`
  padding: 2rem 32px 1.5rem;
`;

const FilterCard = styled.div`
  background: transparent;
  display: flex;
  align-items: flex-end;
  gap: 1rem;
`;

const FilterDropdowns = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex: 1;
  min-width: 0;

  /* The frame (4615:27101) draws the Date Range select at 250px. */
  & > div {
    max-width: 250px;
  }
`;

const ExportButton = styled.button`
  background: ${colors.blue500};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  height: 40px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const Body = styled.div`
  padding: 0 32px 64px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 0.5rem;
`;

const DescriptionText = styled.p`
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin: 0;

  strong {
    font-weight: 700;
  }
`;

const SummaryCards = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0px 1px 3px rgba(14, 26, 49, 0.1),
    0px 1px 2px rgba(14, 26, 49, 0.06);
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SummaryCardFooter = styled.div`
  max-width: 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const LargeLevelTag = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$text};
  border: 1px solid ${(p) => p.$border};
  border-radius: 11px;
  padding: 2px 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
`;

const LevelName = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  text-align: center;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderRow = styled.tr`
  height: 54px;
  background: ${colors.nightlyWoods500};
`;

const headerCellStyles = `
  padding: 16px;
  color: white;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  white-space: nowrap;
`;

// The Student column pins while the weeks scroll. Hard min/max widths keep
// the 159px once auto table layout starts squeezing an overflowing row.
const pinnedColumn = `
  position: sticky;
  left: 0;
  z-index: 1;
  width: 159px;
  min-width: 159px;
  max-width: 159px;
  box-sizing: border-box;
`;

const StudentHeaderCell = styled.th`
  ${headerCellStyles}
  ${pinnedColumn}
  background: ${colors.nightlyWoods500};
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const WeekHeaderCell = styled.th`
  ${headerCellStyles}
`;

const HeaderCellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SortButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
`;

const TableBodyRow = styled.tr`
  height: 62px;
  border-bottom: 1px solid #858b87;
`;

const bodyCellStyles = `
  padding: 0 16px;
  height: 62px;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  vertical-align: middle;
`;

const StudentBodyCell = styled.td`
  ${bodyCellStyles}
  ${pinnedColumn}
  color: #4f4f4f;
  background: ${colors.gray50};
  white-space: nowrap;
`;

const LevelCell = styled.td`
  ${bodyCellStyles}
  background: ${colors.gray50};
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  &:hover {
    ${(p) => p.$clickable && "background: #f5f5f5;"}
  }
`;

const NAText = styled.span`
  color: ${colors.milkGlass950};
  font-size: 14px;
`;

const EmptyRowCell = styled.td`
  ${bodyCellStyles}
  text-align: center;
  background: ${colors.gray50};
  color: ${colors.milkGlass950};
`;

const SkeletonHeaderCell = styled.th`
  ${headerCellStyles}
  background: ${colors.nightlyWoods500};
`;

const SkeletonBodyCell = styled.td`
  ${bodyCellStyles}
  background: ${colors.gray50};
`;

const SkeletonBar = styled.div`
  @keyframes skeleton-pulse {
    0% { opacity: 0.6; }
    50% { opacity: 0.3; }
    100% { opacity: 0.6; }
  }
  width: ${(p) => p.$w || "60px"};
  height: 14px;
  border-radius: 4px;
  background: ${colors.milkGlass600};
  animation: skeleton-pulse 1.5s ease-in-out infinite;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 0;
  font-size: 16px;
  color: ${colors.milkGlass950};
`;
